/**
 * Public account-deletion request form (Item 6, Estero release).
 * Reachable at /fieldproiq/delete-account WITHOUT login.
 * Collects name + email + company + optional reason, forwards to
 * legal@apexinfinityventures.com via the backend.
 */
import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Shield, CheckCircle2, Loader2 } from "lucide-react";

export default function DeleteAccountRequest() {
  const [form, setForm] = useState({ name: "", email: "", company: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError("Name, email, and company are all required.");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/public/delete-account-request", form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit your request. Please email legal@apexinfinityventures.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4 py-10"
         data-testid="delete-account-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-zinc-900 text-white mb-3">
            <Shield className="w-5 h-5" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-900">Request account deletion</h1>
          <p className="text-sm text-zinc-500 mt-2">
            APEX FieldProIQ — submit this form and our team will reach out within 2 business days.
          </p>
        </div>

        <Card className="border border-zinc-200">
          <CardHeader className="pb-3">
            <p className="text-sm text-zinc-600">
              Deleting your account removes your profile, job history, uploaded photos, and signatures from
              our systems. This action cannot be undone. If your organisation has an administrator, please
              contact them first — only they can remove shared records.
            </p>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="text-center py-6" data-testid="delete-account-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                <p className="text-sm font-semibold text-zinc-900">Request submitted</p>
                <p className="text-xs text-zinc-500 mt-2">
                  A confirmation has been emailed to {form.email}. We will follow up within 2 business days.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700"
                       data-testid="delete-account-error">
                    {error}
                  </div>
                )}
                <div>
                  <Label htmlFor="name" className="text-xs font-medium">Full name</Label>
                  <Input id="name" value={form.name} onChange={update("name")}
                         placeholder="Jane Smith" required data-testid="delete-account-name" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-medium">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={update("email")}
                         placeholder="you@company.com" required data-testid="delete-account-email" />
                </div>
                <div>
                  <Label htmlFor="company" className="text-xs font-medium">Company</Label>
                  <Input id="company" value={form.company} onChange={update("company")}
                         placeholder="Your restoration company" required data-testid="delete-account-company" />
                </div>
                <div>
                  <Label htmlFor="reason" className="text-xs font-medium">Reason (optional)</Label>
                  <Textarea id="reason" rows={3} value={form.reason} onChange={update("reason")}
                            placeholder="Anything you'd like us to know?" data-testid="delete-account-reason" />
                </div>
                <Button type="submit" disabled={submitting}
                        className="w-full h-11 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="delete-account-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" />
                             : "Submit deletion request"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[11px] text-zinc-400 mt-4">
          Questions? Email <a href="mailto:legal@apexinfinityventures.com"
          className="underline">legal@apexinfinityventures.com</a> or call 407-484-9100.
        </p>
      </div>
    </div>
  );
}
